/**
 * App Action Registrations
 * Register all application actions with X-Assist ActionExecutor
 * This enables voice commands to execute any app function
 */

import { actionExecutor, AppContext, ExecutionResult } from './ActionExecutor';

/**
 * Register all app actions
 * Call this during app initialization
 */
export function registerAllActions(appCallbacks: AppActionCallbacks): void {

  // ==================== NAVIGATION ACTIONS ====================
  
  actionExecutor.registerAction({
    id: 'navigate_to_dashboard',
    type: 'navigate',
    target: 'dashboard',
    handler: async (params, context) => {
      appCallbacks.navigateToDashboard();
      return {
        success: true,
        action: 'navigate_to_dashboard',
        message: 'Navigating to dashboard',
        navigationTarget: 'dashboard'
      };
    },
    requiredParams: [],
    examples: [
      'go to dashboard',
      'show dashboard',
      'open main screen',
      'go home'
    ],
    tenantIsolated: false
  });

  actionExecutor.registerAction({
    id: 'navigate_to_wells',
    type: 'navigate',
    target: 'wells',
    handler: async (params, context) => {
      appCallbacks.showWellsList();
      return {
        success: true,
        action: 'navigate_to_wells',
        message: 'Showing wells list',
        navigationTarget: 'wells'
      };
    },
    requiredParams: [],
    examples: [
      'show wells',
      'view all wells',
      'go to wells',
      'list wells'
    ],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'navigate_to_well',
    type: 'navigate',
    target: 'well',
    handler: async (params, context) => {
      if (!params.wellId && !params.wellName) {
        return {
          success: false,
          action: 'navigate_to_well',
          message: 'Well not specified',
          error: 'Please specify which well'
        };
      }

      const well = context.wells?.find(w => 
        w.id === params.wellId || 
        w.name.toLowerCase().includes(params.wellName?.toLowerCase())
      );

      if (!well) {
        return {
          success: false,
          action: 'navigate_to_well',
          message: 'Well not found',
          error: `Could not find well: ${params.wellName || params.wellId}`
        };
      }

      appCallbacks.selectWell(well);
      return {
        success: true,
        action: 'navigate_to_well',
        message: `Showing ${well.name}`,
        data: well,
        navigationTarget: 'well-detail'
      };
    },
    requiredParams: [],
    optionalParams: ['wellId', 'wellName'],
    examples: [
      'show well Alpha 5',
      'go to well Bravo 12',
      'open well details'
    ],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'show_route',
    type: 'navigate',
    target: 'route',
    handler: async (params, context) => {
      appCallbacks.showRouteOptimizer();
      return {
        success: true,
        action: 'show_route',
        message: 'Showing route optimizer',
        navigationTarget: 'route'
      };
    },
    requiredParams: [],
    examples: [
      'show my route',
      'view route',
      'show route optimizer',
      'optimize route'
    ],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'show_tickets',
    type: 'navigate',
    target: 'tickets',
    handler: async (params, context) => {
      appCallbacks.showServiceTickets();
      return {
        success: true,
        action: 'show_tickets',
        message: 'Showing service tickets',
        navigationTarget: 'tickets'
      };
    },
    requiredParams: [],
    examples: [
      'show tickets',
      'view service tickets',
      'go to tickets',
      'show maintenance'
    ],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'show_alerts',
    type: 'navigate',
    target: 'alerts',
    handler: async (params, context) => {
      appCallbacks.showAlerts(params.unread);
      return {
        success: true,
        action: 'show_alerts',
        message: 'Showing alerts',
        navigationTarget: 'alerts'
      };
    },
    requiredParams: [],
    optionalParams: ['unread'],
    examples: [
      'show alerts',
      'view notifications',
      'check alerts',
      'show unread alerts'
    ],
    tenantIsolated: true
  });

  // ==================== DATA ENTRY ACTIONS ====================

  actionExecutor.registerAction({
    id: 'create_well_entry',
    type: 'create',
    target: 'entry',
    handler: async (params, context) => {
      if (!params.wellId) {
        return {
          success: false,
          action: 'create_well_entry',
          message: 'Well not specified',
          error: 'Please specify which well'
        };
      }

      const well = context.wells?.find(w => w.id === params.wellId);
      if (!well) {
        return {
          success: false,
          action: 'create_well_entry',
          message: 'Well not found',
          error: `Could not find well: ${params.wellId}`
        };
      }

      // Pre-fill form with voice data
      const formData: any = {
        wellId: well.id,
        wellName: well.name
      };

      if (params.oilVolume) formData.tank1Level = params.oilVolume;
      if (params.waterVolume) formData.tank2Level = params.waterVolume;
      if (params.gasVolume) formData.gasVolume = params.gasVolume;
      if (params.pressure) formData.tubingPressure = params.pressure;

      appCallbacks.openDataEntry(well, formData);

      return {
        success: true,
        action: 'create_well_entry',
        message: `Opening entry form for ${well.name}`,
        data: well,
        formData,
        navigationTarget: 'data-entry',
        nextActions: ['Save entry', 'Add voice memo', 'Take photo']
      };
    },
    requiredParams: [],
    optionalParams: ['wellId', 'wellName', 'oilVolume', 'waterVolume', 'gasVolume', 'pressure'],
    examples: [
      'add entry for well Alpha 5',
      'create production data for well 12',
      'enter 25 barrels oil and 15 water for well Alpha',
      'add entry with tubing pressure 350'
    ],
    permissions: ['create_entry'],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'show_drafts',
    type: 'navigate',
    target: 'drafts',
    handler: async (params, context) => {
      appCallbacks.showDrafts();
      return {
        success: true,
        action: 'show_drafts',
        message: 'Showing draft entries',
        navigationTarget: 'drafts'
      };
    },
    requiredParams: [],
    examples: [
      'show drafts',
      'show incomplete entries',
      'view my drafts',
      'show unfinished work'
    ],
    tenantIsolated: true
  });

  // ==================== SERVICE TICKET ACTIONS ====================

  actionExecutor.registerAction({
    id: 'create_ticket',
    type: 'create',
    target: 'ticket',
    handler: async (params, context) => {
      const formData: any = {};

      if (params.wellId) formData.wellId = params.wellId;
      if (params.issueType) formData.type = params.issueType;
      if (params.priority) formData.priority = params.priority;
      if (params.description) formData.description = params.description;
      if (params.date) formData.scheduledDate = params.date;

      appCallbacks.createServiceTicket(formData);

      return {
        success: true,
        action: 'create_ticket',
        message: 'Creating service ticket',
        formData,
        navigationTarget: 'ticket-form'
      };
    },
    requiredParams: [],
    optionalParams: ['wellId', 'issueType', 'priority', 'description', 'date'],
    examples: [
      'create service ticket',
      'schedule maintenance for well Alpha 5',
      'create emergency ticket for pump failure',
      'schedule service for next Tuesday'
    ],
    permissions: ['create_ticket'],
    tenantIsolated: true
  });

  // ==================== FILTER ACTIONS ====================

  actionExecutor.registerAction({
    id: 'filter_wells_overdue',
    type: 'filter',
    target: 'wells',
    handler: async (params, context) => {
      appCallbacks.filterWells({ status: 'overdue' });
      return {
        success: true,
        action: 'filter_wells_overdue',
        message: 'Filtering overdue wells',
        navigationTarget: 'wells'
      };
    },
    requiredParams: [],
    examples: [
      'show overdue wells',
      'filter overdue',
      'wells that need checking'
    ],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'filter_wells',
    type: 'filter',
    target: 'wells',
    handler: async (params, context) => {
      appCallbacks.filterWells(params);
      return {
        success: true,
        action: 'filter_wells',
        message: `Filtering wells by ${Object.keys(params).join(', ')}`,
        data: params,
        navigationTarget: 'wells'
      };
    },
    requiredParams: [],
    optionalParams: ['status', 'type', 'assignedTo'],
    examples: [
      'show active wells',
      'filter by status',
      'show my assigned wells'
    ],
    tenantIsolated: true
  });

  // ==================== ROUTE ACTIONS ====================

  actionExecutor.registerAction({
    id: 'create_route',
    type: 'create',
    target: 'route',
    handler: async (params, context) => {
      appCallbacks.createRoute(params.wells);
      return {
        success: true,
        action: 'create_route',
        message: 'Creating optimized route',
        navigationTarget: 'route'
      };
    },
    requiredParams: [],
    optionalParams: ['wells'],
    examples: [
      'create route',
      'optimize route',
      'create route from all wells'
    ],
    permissions: ['create_route'],
    tenantIsolated: true
  });

  // ==================== UTILITY ACTIONS ====================

  actionExecutor.registerAction({
    id: 'clock_in',
    type: 'execute',
    target: 'timesheet',
    handler: async (params, context) => {
      appCallbacks.clockIn();
      return {
        success: true,
        action: 'clock_in',
        message: 'Clocked in successfully'
      };
    },
    requiredParams: [],
    examples: [
      'clock in',
      'start shift',
      'punch in'
    ],
    tenantIsolated: true
  });

  actionExecutor.registerAction({
    id: 'clock_out',
    type: 'execute',
    target: 'timesheet',
    handler: async (params, context) => {
      appCallbacks.clockOut();
      return {
        success: true,
        action: 'clock_out',
        message: 'Clocked out successfully'
      };
    },
    requiredParams: [],
    examples: [
      'clock out',
      'end shift',
      'punch out'
    ],
    tenantIsolated: true
  });

  const registeredCount = actionExecutor.getAllActions().length;
}

/**
 * Callback interface that App.tsx must implement
 */
export interface AppActionCallbacks {
  // Navigation
  navigateToDashboard: () => void;
  showWellsList: () => void;
  selectWell: (well: any) => void;
  showRouteOptimizer: () => void;
  showServiceTickets: () => void;
  showAlerts: (unreadOnly?: boolean) => void;
  showDrafts: () => void;

  // Data Entry
  openDataEntry: (well: any, formData?: any) => void;

  // Service Tickets
  createServiceTicket: (formData?: any) => void;

  // Filters
  filterWells: (filters: any) => void;

  // Routes
  createRoute: (wells?: any[]) => void;

  // Timesheet
  clockIn: () => void;
  clockOut: () => void;
}
